<script lang="ts">
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import { page } from '$app/stores';
	import type { UnionIconValues } from '@repo/common';
	import { user } from '@repo/sharedlib/src/stores/auth.store';
	import constants from '@repo/sharedlib/src/utils/constants';
	import { Button } from '@repo/ui/src/components/ui/button';
	import IonIcon from '@repo/ui/src/components/ui/IonIcon.svelte';
	import * as Sheet from '@repo/ui/src/components/ui/sheet';
	import { onMount } from 'svelte';
	import { FEATURE_FLAGS } from '@repo/sharedlib/src/utils/constants';

	interface Props {
		signInHandler: () => void;
		signUpHandler: () => void;
		signOut: () => void;
		smoothScrollTo: (elementId: string) => void;
		platform?: 'macos' | 'windows';
	}

	let {
		signInHandler,
		signUpHandler,
		signOut,
		smoothScrollTo,
		platform = 'macos'
	}: Props = $props();

	let isOpen = $state(false);
	let searchQuery = $state('');

	// Track mount state to avoid hydration issues with Sheet component
	let mounted = $state(false);
	onMount(() => {
		mounted = true;
	});

	// Determine if we're on a help route - this controls User Guide expansion
	let isHelpRoute = $derived($page.url.pathname.startsWith('/help'));

	// Define searchable pages (same as HelpSearchCommand)
	const sitemapPages = [
		// Help/Docs pages
		{
			title: 'User Guide Home',
			url: '/help',
			category: 'Documentation',
			description: 'Master your second brain with Retain'
		},
		{
			title: 'Introduction',
			url: '/help/article/introduction',
			category: 'Documentation',
			description: 'Discover how Retain becomes your productivity superpower'
		},
		{
			title: 'Quick Start Guide',
			url: '/help/article/quick-start',
			category: 'Documentation',
			description: 'Start capturing, organizing, and acting in minutes'
		},
		{
			title: 'Installation',
			url: '/help/article/retain-app-installation-guide',
			category: 'Documentation',
			description: 'Get Retain running on your Mac in seconds'
		},
		{
			title: 'Calendar Integration',
			url: '/help/article/retain-app-calendar-guide',
			category: 'Documentation',
			description: 'Connect your calendar and let Retain automatically capture meeting notes'
		},
		{
			title: 'Email Management',
			url: '/help/article/retain-app-email-management-guide',
			category: 'Documentation',
			description: 'Draft, send, and organize emails with AI assistance'
		},
		{
			title: 'Meeting Capture',
			url: '/help/article/retain-app-how-to-capture-meetings-with-retain',
			category: 'Documentation',
			description: 'Record and transcribe meetings, automatically generate summaries'
		},
		{
			title: 'Email Expertise',
			url: '/help/article/write-more-effective-emails-expertise',
			category: 'Documentation',
			description: 'Learn advanced techniques for writing more effective emails'
		},
		{
			title: 'AI Assistant',
			url: '/help/article/ai-assistant',
			category: 'Documentation',
			description: 'Use AI to organize, remember, and act on your information'
		},
		{
			title: 'Workspaces',
			url: '/help/article/workspaces',
			category: 'Documentation',
			description: 'Organize your work into separate spaces'
		},
		{
			title: 'Integrations',
			url: '/help/article/integrations',
			category: 'Documentation',
			description: 'Connect Retain with your favorite tools'
		},
		{
			title: 'Account Management',
			url: '/help/article/retain-app-account-management',
			category: 'Documentation',
			description: 'Manage your Retain account settings'
		},
		{
			title: 'FAQ',
			url: '/help/article/faq',
			category: 'Documentation',
			description: 'Frequently asked questions about Retain'
		},
		// Main site pages
		{
			title: 'Home',
			url: '/',
			category: 'Pages',
			description: 'The only productivity app you will ever need'
		},
		{
			title: 'Blog',
			url: '/blog',
			category: 'Pages',
			description: 'Latest updates and insights from Retain'
		},
		{
			title: 'Pricing',
			url: '/pricing',
			category: 'Pages',
			description: 'Choose the perfect plan for your needs'
		},
		{
			title: 'Download',
			url: '/download',
			category: 'Pages',
			description: 'Download Retain for Mac'
		},
		{
			title: 'Sign up',
			url: '/sign-up',
			category: 'Pages',
			description: 'Create your Retain account'
		},
		{
			title: 'Sign in',
			url: '/sign-in',
			category: 'Pages',
			description: 'Sign in to your account'
		},
		{
			title: 'Privacy Policy',
			url: '/privacy-policy',
			category: 'Pages',
			description: 'How we protect your data'
		},
		{
			title: 'Terms of Service',
			url: '/terms-of-service',
			category: 'Pages',
			description: 'Terms and conditions'
		}
	];

	// Search results using same logic as HelpSearchCommand
	let searchResults = $derived.by(() => {
		if (!searchQuery.trim()) return [];

		const lowerQuery = searchQuery.toLowerCase();

		return sitemapPages
			.map((page) => {
				const titleLower = page.title.toLowerCase();
				const descLower = page.description.toLowerCase();

				let score = 0;
				if (titleLower === lowerQuery) score += 100;
				else if (titleLower.includes(lowerQuery)) score += 50;
				if (descLower.includes(lowerQuery)) score += 25;

				const queryWords = lowerQuery.split(' ');
				queryWords.forEach((word) => {
					if (titleLower.includes(word)) score += 10;
					if (descLower.includes(word)) score += 5;
				});

				return { ...page, score };
			})
			.filter((p) => p.score > 0)
			.sort((a, b) => b.score - a.score)
			.slice(0, 8);
	});

	// Check if search is active
	let isSearching = $derived(searchQuery.trim().length > 0);

	// User Guide sections (same as in help layout)
	const userGuideSections = [
		{
			title: 'Getting Started',
			items: [
				{
					title: 'Introduction',
					href: '/help/article/introduction',
					icon: 'book' as UnionIconValues
				},
				{
					title: 'Quick Start Guide',
					href: '/help/article/quick-start',
					icon: 'bolt' as UnionIconValues
				},
				{
					title: 'Installation',
					href: '/help/article/retain-app-installation-guide',
					icon: 'arrow_down_circle' as UnionIconValues
				}
			]
		},
		{
			title: 'Core Features',
			items: [
				{
					title: 'Calendar Integration',
					href: '/help/article/retain-app-calendar-guide',
					icon: 'calendar' as UnionIconValues
				},
				{
					title: 'Email Management',
					href: '/help/article/retain-app-email-management-guide',
					icon: 'envelope' as UnionIconValues
				},
				{
					title: 'Meeting Capture',
					href: '/help/article/retain-app-how-to-capture-meetings-with-retain',
					icon: 'video' as UnionIconValues
				},
				{
					title: 'Effective Emails',
					href: '/help/article/write-more-effective-emails-expertise',
					icon: 'sparkles' as UnionIconValues
				}
			]
		},
		{
			title: 'Advanced Features',
			items: [
				{
					title: 'Email Expertise',
					href: '/help/article/write-more-effective-emails-expertise',
					icon: 'lightbulb' as UnionIconValues
				},
				{
					title: 'Workspaces',
					href: '/help/article/workspaces',
					icon: 'folder' as UnionIconValues
				},
				{
					title: 'Keyboard Shortcuts',
					href: '/help/article/keyboard-shortcuts',
					icon: 'gearshape' as UnionIconValues
				}
			]
		},
		{
			title: 'Account & Settings',
			items: [
				{
					title: 'Account Management',
					href: '/help/article/retain-app-account-management',
					icon: 'person' as UnionIconValues
				},
				{
					title: 'Subscriptions & Billing',
					href: '/help/article/subscriptions',
					icon: 'creditcard' as UnionIconValues
				},
				{
					title: 'Privacy & Security',
					href: '/help/article/privacy-security',
					icon: 'checkmark_shield' as UnionIconValues
				},
				{
					title: 'Data Export',
					href: '/help/article/data-export',
					icon: 'icloud_and_arrow_up' as UnionIconValues
				}
			]
		},
		{
			title: 'Troubleshooting',
			items: [
				{
					title: 'Common Issues',
					href: '/help/article/common-issues',
					icon: 'wrench_and_screwdriver' as UnionIconValues
				},
				{ title: 'FAQ', href: '/help/article/faq', icon: 'questionmark_circle' as UnionIconValues },
				{
					title: 'Contact Support',
					href: '/help/article/contact-support',
					icon: 'envelope' as UnionIconValues
				}
			]
		}
	];

	function handleNavClick(href: string) {
		isOpen = false;
		searchQuery = '';
		if (href.startsWith('http')) {
			window.location.href = href;
		} else {
			goto(href);
		}
	}

	function handleScrollClick(elementId: string) {
		isOpen = false;
		searchQuery = '';
		smoothScrollTo(elementId);
	}

	function isActive(href: string) {
		return $page.url.pathname === href;
	}

	function isActiveRoute(route: string) {
		return $page.url.pathname.startsWith(route);
	}

	// Highlight matching text
	function highlightText(text: string, query: string): string {
		if (!query) return text;

		const lowerText = text.toLowerCase();
		const lowerQuery = query.toLowerCase();

		if (!lowerText.includes(lowerQuery)) {
			let result = text;
			query.split(' ').forEach((word) => {
				const regex = new RegExp(`(${word})`, 'gi');
				result = result.replace(regex, '<span class="text-green-400 font-semibold">$1</span>');
			});
			return result;
		}

		const startIndex = lowerText.indexOf(lowerQuery);
		const endIndex = startIndex + query.length;

		return (
			text.substring(0, startIndex) +
			'<span class="text-green-400 font-semibold">' +
			text.substring(startIndex, endIndex) +
			'</span>' +
			text.substring(endIndex)
		);
	}

	function handleAskAI() {
		// TODO: Implement Ask Retain AI functionality
		console.log('Ask Retain AI:', searchQuery);
		isOpen = false;
	}

	let userName = $derived($user?.firstName ? `${$user.firstName} ${$user.lastName}` : $user?.email);

	// Get platform-specific download URL
	let downloadUrl = $derived(
		platform === 'windows' ? constants.DOWNLOAD_URL_WINDOWS : constants.DOWNLOAD_URL_MACOS
	);
</script>

<!-- Mobile menu button - always visible on mobile -->
<div class="flex md:hidden">
	{#if mounted}
		<Sheet.Root bind:open={isOpen}>
			<Sheet.Trigger>
				<button
					aria-label="Toggle mobile menu"
					class="flex flex-col justify-center items-center w-8 h-8 space-y-1"
				>
					<div
						class="w-6 h-0.5 bg-white transition-all duration-300 {isOpen
							? 'rotate-45 translate-y-1.5'
							: ''}"
					></div>
					<div
						class="w-6 h-0.5 bg-white transition-all duration-300 {isOpen ? 'opacity-0' : ''}"
					></div>
					<div
						class="w-6 h-0.5 bg-white transition-all duration-300 {isOpen
							? '-rotate-45 -translate-y-1.5'
							: ''}"
					></div>
				</button>
			</Sheet.Trigger>

			<Sheet.Content
				side="right"
				class="w-full sm:max-w-md bg-gray-950 text-white border-gray-800 p-0"
			>
				<div class="flex h-full flex-col">
					<!-- Header with Profile -->
					<div class="border-b border-gray-800 px-6 py-4">
						{#if $user?.id}
							<div class="flex items-center gap-3">
								{#if $user.picture}
									<img src={$user.picture} alt="Profile" class="h-10 w-10 rounded-full" />
								{:else}
									<div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600">
										<span class="text-sm font-semibold text-white">
											{$user?.firstName?.[0] || $user?.email?.[0] || 'U'}
										</span>
									</div>
								{/if}
								<div class="flex-1 min-w-0">
									<div class="truncate text-sm font-medium text-white">{userName}</div>
									<div class="truncate text-xs text-gray-400">{$user.email}</div>
								</div>
							</div>
						{:else}
							<div class="text-lg font-semibold text-white">Navigation</div>
						{/if}
					</div>

					<!-- Content -->
					<div class="flex-1 overflow-y-auto px-6 py-4">
						<!-- Search -->
						<div class="mb-6">
							<div class="relative">
								<IonIcon
									name="magnifyingglass"
									myClass="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
								/>
								<input
									type="search"
									bind:value={searchQuery}
									placeholder="Search..."
									class="h-10 w-full rounded-lg bg-gray-900 pl-10 pr-4 text-sm text-white placeholder:text-gray-500 outline-none ring-1 ring-gray-800 focus:ring-2 focus:ring-blue-500"
								/>
								{#if searchQuery}
									<button
										onclick={() => (searchQuery = '')}
										class="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white"
									>
										<IonIcon name="xmark_circle_fill" myClass="h-4 w-4" />
									</button>
								{/if}
							</div>
						</div>

						<!-- Search Results (shown when searching) -->
						{#if isSearching}
							<div class="space-y-2">
								{#if searchResults.length > 0}
									<div class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
										Search Results
									</div>
									{#each searchResults as result (result.url)}
										<button
											onclick={() => handleNavClick(result.url)}
											class="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-900"
										>
											<div
												class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-cyan-600"
											>
												<IonIcon
													name={result.category === 'Documentation' ? 'book' : 'link'}
													myClass="h-4 w-4 text-white"
												/>
											</div>
											<div class="min-w-0 flex-1">
												<p class="text-sm font-medium text-white">
													{@html highlightText(result.title, searchQuery)}
												</p>
												<p class="mt-0.5 text-xs text-gray-400 line-clamp-2">
													{@html highlightText(result.description, searchQuery)}
												</p>
												<p class="mt-1 text-xs text-gray-500">
													{result.category}
												</p>
											</div>
										</button>
									{/each}

									<!-- Ask Retain AI Button -->
									<button
										onclick={handleAskAI}
										class="mt-4 flex w-full items-center gap-3 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 px-3 py-2.5 text-white transition-all hover:from-purple-600 hover:to-blue-600"
									>
										<div
											class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white/20"
										>
											<IonIcon name="sparkles" myClass="h-4 w-4 text-white" />
										</div>
										<div class="flex-1 text-left">
											<p class="text-sm font-medium">Ask Retain AI</p>
											<p class="text-xs text-white/80">Get AI-powered answers</p>
										</div>
									</button>
								{:else}
									<div class="text-center py-8">
										<div
											class="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-gray-900"
										>
											<IonIcon name="magnifyingglass" myClass="h-6 w-6 text-gray-500" />
										</div>
										<p class="mt-4 text-sm font-medium text-white">No results found</p>
										<p class="mt-1 text-xs text-gray-400">Try a different search term</p>

										<!-- Ask Retain AI Button in empty state -->
										<button
											onclick={handleAskAI}
											class="mt-4 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 px-4 py-2 text-sm font-medium text-white transition-all hover:from-purple-600 hover:to-blue-600"
										>
											<IonIcon name="sparkles" myClass="h-4 w-4" />
											Ask Retain AI
										</button>
									</div>
								{/if}
							</div>
						{:else}
							<!-- Navigation Items (shown when not searching) -->
							<nav class="space-y-1">
								<!-- Main Navigation -->
								<!-- Web app -->
								{#if $user?.id && FEATURE_FLAGS['web-app']}
									<div class="space-y-1">
										<a
											href={resolve('/app/start')}
											rel="external"
											target="retainApp"
											class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors {isActiveRoute(
												'/app/start'
											)
												? 'bg-green-900/20 text-green-400'
												: 'text-gray-300 hover:bg-gray-900 hover:text-white'}"
										>
											Web App
											{#if isActiveRoute('/app/start')}
												<div class="ml-auto h-1.5 w-1.5 rounded-full bg-green-400"></div>
											{/if}
										</a>
									</div>
								{/if}

								<button
									onclick={() => handleScrollClick('meeting-notes')}
									class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-300 transition-colors hover:bg-gray-900 hover:text-white"
								>
									<IonIcon name="sparkles" myClass="h-5 w-5" />
									Features
								</button>

								<!-- User Guide Section - Auto-expanded only on /help routes -->
								<div class="space-y-1">
									<button
										onclick={() => handleNavClick('/help')}
										class="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors {isHelpRoute
											? 'bg-green-900/20 text-green-400'
											: 'text-gray-300 hover:bg-gray-900 hover:text-white'}"
									>
										<div class="flex items-center gap-3">
											<IonIcon name="book" myClass="h-5 w-5" />
											User Guide
										</div>
										{#if isHelpRoute}
											<IonIcon name="chevron-down" myClass="h-4 w-4" />
										{:else}
											<IonIcon name="chevron_right" myClass="h-4 w-4" />
										{/if}
									</button>

									<!-- User Guide content - only shown when on /help routes -->
									{#if isHelpRoute}
										<div class="ml-2 space-y-1 border-l-2 border-gray-800 pl-2">
											{#each userGuideSections as section}
												<div class="space-y-1">
													<div
														class="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500"
													>
														{section.title}
													</div>
													{#each section.items as item}
														<button
															onclick={() => handleNavClick(item.href)}
															class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors {isActive(
																item.href
															)
																? 'bg-green-900/20 text-green-400'
																: 'text-gray-400 hover:bg-gray-900 hover:text-white'}"
														>
															<IonIcon name={item.icon} myClass="h-4 w-4" />
															<span class="flex-1 truncate">{item.title}</span>
															{#if isActive(item.href)}
																<div class="h-1.5 w-1.5 rounded-full bg-green-400"></div>
															{/if}
														</button>
													{/each}
												</div>
											{/each}
										</div>
									{/if}
								</div>

								<button
									onclick={() => handleNavClick('/pricing')}
									class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors {isActiveRoute(
										'/pricing'
									)
										? 'bg-green-900/20 text-green-400'
										: 'text-gray-300 hover:bg-gray-900 hover:text-white'}"
								>
									<IonIcon name="pricetag-outline" myClass="h-5 w-5" />
									Pricing
									{#if isActiveRoute('/pricing')}
										<div class="ml-auto h-1.5 w-1.5 rounded-full bg-green-400"></div>
									{/if}
								</button>

								<button
									onclick={() => handleNavClick(resolve('/blog'))}
									class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors {isActiveRoute(
										'/blog'
									)
										? 'bg-green-900/20 text-green-400'
										: 'text-gray-300 hover:bg-gray-900 hover:text-white'}"
								>
									<IonIcon name="text_document" myClass="h-5 w-5" />
									Blog
									{#if isActiveRoute('/blog')}
										<div class="ml-auto h-1.5 w-1.5 rounded-full bg-green-400"></div>
									{/if}
								</button>

								<button
									onclick={() => handleNavClick(downloadUrl)}
									class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors {isActiveRoute(
										'/download'
									)
										? 'bg-green-900/20 text-green-400'
										: 'text-gray-300 hover:bg-gray-900 hover:text-white'}"
								>
									<IonIcon name="arrow_down_circle" myClass="h-5 w-5" />
									Download
									{#if isActiveRoute('/download')}
										<div class="ml-auto h-1.5 w-1.5 rounded-full bg-green-400"></div>
									{/if}
								</button>
							</nav>

							<!-- Divider -->
							<div class="my-6 border-t border-gray-800"></div>

							<!-- Account Actions for Authenticated Users -->
							{#if $user?.id}
								<div class="space-y-1">
									<button
										onclick={() => handleNavClick(resolve('/account'))}
										class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors {isActiveRoute(
											'/account'
										)
											? 'bg-green-900/20 text-green-400'
											: 'text-gray-300 hover:bg-gray-900 hover:text-white'}"
									>
										<IonIcon name="person" myClass="h-5 w-5" />
										Account settings
										{#if isActiveRoute('/account')}
											<div class="ml-auto h-1.5 w-1.5 rounded-full bg-green-400"></div>
										{/if}
									</button>

									<button
										onclick={() => {
											isOpen = false;
											signOut();
										}}
										class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-400 transition-colors hover:bg-red-900/20 hover:text-red-300"
									>
										<IonIcon name="arrow_right" myClass="h-5 w-5" />
										Sign out
									</button>
								</div>
							{:else}
								<!-- Sign In/Up for Unauthenticated Users -->
								<div class="space-y-2">
									<Button
										onclick={() => {
											isOpen = false;
											signInHandler();
										}}
										variant="gray"
										class="w-full gap-2"
									>
										<IonIcon name="person" myClass="h-5 w-5" />
										Sign in
									</Button>

									<Button
										onclick={() => {
											isOpen = false;
											signUpHandler();
										}}
										class="w-full gap-2 bg-blue-600 hover:bg-blue-700"
									>
										<IonIcon name="sparkles" myClass="h-5 w-5" />
										Sign up
									</Button>
								</div>
							{/if}
						{/if}
					</div>

					<!-- Footer -->
					<div class="border-t border-gray-800 px-6 py-4">
						<div class="text-center text-xs text-gray-500">
							© 2025 Retain AI. All rights reserved.
						</div>
					</div>
				</div>
			</Sheet.Content>
		</Sheet.Root>
	{:else}
		<!-- SSR fallback - just show the hamburger button placeholder -->
		<button
			aria-label="Toggle mobile menu"
			class="flex flex-col justify-center items-center w-8 h-8 space-y-1"
		>
			<div class="w-6 h-0.5 bg-white"></div>
			<div class="w-6 h-0.5 bg-white"></div>
			<div class="w-6 h-0.5 bg-white"></div>
		</button>
	{/if}
</div>
